import { useCallback, useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, loadSession } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

type ForwardingState = Awaited<ReturnType<typeof api.workspaceForwarding>>;
type Destination = ForwardingState["destinations"][number];
type Schedule = ForwardingState["schedules"][number];
type EnvelopesResponse = Awaited<ReturnType<typeof api.workspaceForwardingEnvelopes>>;
type Envelope = EnvelopesResponse["envelopes"][number];

type Mode =
  | { kind: "none" }
  | { kind: "create-destination" }
  | { kind: "edit-destination"; destination: Destination }
  | { kind: "create-schedule"; destinationId: string };

const DESTINATION_KINDS: Destination["kind"][] = ["dhis2", "webhook", "warehouse"];
const DESTINATION_STATUSES: Destination["status"][] = ["active", "paused", "disabled"];

export default function Forwarding() {
  const session = loadSession();
  const workspaceId = session?.workspaceId ?? "";
  const [data, setData] = useState<ForwardingState | null>(null);
  const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<Mode>({ kind: "none" });

  const load = useCallback(() => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.workspaceForwarding(workspaceId),
      api.workspaceForwardingEnvelopes(workspaceId, { limit: 25 }).catch(() => ({ envelopes: [] }))
    ])
      .then(([forwarding, envelopesPayload]) => {
        setData(forwarding);
        setEnvelopes(envelopesPayload.envelopes);
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  async function handleTestDestination(destinationId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.testForwardingDestination(workspaceId, destinationId);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRetryEnvelope(envelopeId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.retryForwardingEnvelope(workspaceId, envelopeId);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!workspaceId) {
    return <div className="page"><div className="alert alert-error">No workspace in session.</div></div>;
  }

  const destinations = data?.destinations ?? [];
  const schedules    = data?.schedules ?? [];
  const recentRuns   = data?.recentRuns ?? [];

  const tDestinations = useTableState(destinations as unknown as Array<Record<string, unknown>>, {
    searchFields: ["label", "kind", "target", "status"],
    initialPageSize: 25
  });
  const tSchedules = useTableState(schedules as unknown as Array<Record<string, unknown>>, {
    searchFields: ["label", "destinationId", "cron"],
    initialPageSize: 25
  });
  const tEnvelopes = useTableState(envelopes as unknown as Array<Record<string, unknown>>, {
    searchFields: ["destinationId", "status", "payloadChecksum", "lastError"],
    initialPageSize: 25
  });
  const tRuns = useTableState(recentRuns as unknown as Array<Record<string, unknown>>, {
    searchFields: ["destinationId", "status", "message"],
    initialPageSize: 25
  });

  async function handleDeleteDestination(destinationId: string) {
    if (!confirm("Delete this destination? Associated schedules will be removed.")) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteForwardingDestination(workspaceId, destinationId);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handlePauseToggle(d: Destination) {
    setBusy(true);
    setError(null);
    try {
      const next = d.status === "active" ? "paused" : "active";
      await api.updateForwardingDestination(workspaceId, d.id, { status: next });
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteSchedule(scheduleId: string) {
    if (!confirm("Delete this schedule?")) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteForwardingSchedule(workspaceId, scheduleId);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Forwarding hub</h1>
          <p>Outbound destinations that receive data from this workspace — remote DHIS2 instances, webhooks, or external warehouses.</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => setMode({ kind: "create-destination" })}>
            New destination
          </button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {mode.kind === "create-destination" && (
        <DestinationForm
          workspaceId={workspaceId}
          onClose={() => setMode({ kind: "none" })}
          onSaved={() => { setMode({ kind: "none" }); load(); }}
          onError={setError}
        />
      )}
      {mode.kind === "edit-destination" && (
        <DestinationForm
          workspaceId={workspaceId}
          destination={mode.destination}
          onClose={() => setMode({ kind: "none" })}
          onSaved={() => { setMode({ kind: "none" }); load(); }}
          onError={setError}
        />
      )}
      {mode.kind === "create-schedule" && (
        <ScheduleForm
          workspaceId={workspaceId}
          destinationId={mode.destinationId}
          onClose={() => setMode({ kind: "none" })}
          onSaved={() => { setMode({ kind: "none" }); load(); }}
          onError={setError}
        />
      )}

      {loading ? <SkeletonTable rows={5} columns={4} /> : (
        <>
          <section>
            <h2>Destinations</h2>
            {destinations.length === 0 ? (
              <div className="empty">
                <p><strong>No forwarding destinations configured.</strong></p>
                <p className="muted">Create a destination to start forwarding tallies to DHIS2 instances, webhooks, or warehouses.</p>
              </div>
            ) : (
              <>
              <TableToolbar
                search={tDestinations.search} setSearch={tDestinations.setSearch}
                page={tDestinations.page} setPage={tDestinations.setPage}
                pageSize={tDestinations.pageSize} setPageSize={tDestinations.setPageSize}
                totalPages={tDestinations.totalPages} totalRows={tDestinations.totalRows}
                hideExport
                searchPlaceholder="Search destinations…"
              />
              <table className="data-table">
                <thead><tr><th>Label</th><th>Kind</th><th>Target</th><th>Status</th><th>Updated</th><th></th></tr></thead>
                <tbody>
                  {(tDestinations.paged as unknown as Destination[]).map((d) => (
                    <tr key={d.id}>
                      <td>{d.label}</td>
                      <td className="soft">{d.kind}</td>
                      <td className="soft"><code>{d.target}</code></td>
                      <td><span className={`pill ${d.status === "active" ? "pill-success" : d.status === "paused" ? "pill-warning" : "pill-muted"}`}>{d.status}</span></td>
                      <td className="soft">{new Date(d.updatedAt).toLocaleString()}</td>
                      <td>
                        <div className="row-actions">
                          <button type="button" className="btn btn-link" disabled={busy} onClick={() => setMode({ kind: "edit-destination", destination: d as Destination })}>Edit</button>
                          <button type="button" className="btn btn-link" disabled={busy} onClick={() => handlePauseToggle(d as Destination)}>
                            {d.status === "active" ? "Pause" : "Resume"}
                          </button>
                          <button type="button" className="btn btn-link" disabled={busy} onClick={() => setMode({ kind: "create-schedule", destinationId: d.id })}>Add schedule</button>
                          <button type="button" className="btn btn-link" disabled={busy} onClick={() => handleTestDestination(d.id)}>Test send</button>
                          <button type="button" className="btn btn-link danger" disabled={busy} onClick={() => handleDeleteDestination(d.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </section>

          <section>
            <h2>Schedules</h2>
            {schedules.length === 0 ? (
              <p className="muted">No schedules configured. Add one to a destination above.</p>
            ) : (
              <>
              <TableToolbar
                search={tSchedules.search} setSearch={tSchedules.setSearch}
                page={tSchedules.page} setPage={tSchedules.setPage}
                pageSize={tSchedules.pageSize} setPageSize={tSchedules.setPageSize}
                totalPages={tSchedules.totalPages} totalRows={tSchedules.totalRows}
                hideExport
                searchPlaceholder="Search schedules…"
              />
              <table className="data-table">
                <thead><tr><th>Label</th><th>Destination</th><th>Cron</th><th>Enabled</th><th></th></tr></thead>
                <tbody>
                  {(tSchedules.paged as unknown as Schedule[]).map((s: Schedule) => (
                    <tr key={s.id}>
                      <td>{s.label}</td>
                      <td className="soft">{destinations.find((d) => d.id === s.destinationId)?.label ?? s.destinationId}</td>
                      <td className="soft"><code>{s.cron}</code></td>
                      <td><span className={`pill ${s.enabled ? "pill-success" : "pill-muted"}`}>{s.enabled ? "enabled" : "disabled"}</span></td>
                      <td>
                        <button type="button" className="btn btn-link danger" disabled={busy} onClick={() => handleDeleteSchedule(s.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </section>

          <section>
            <h2>Recent transfers</h2>
            <p className="muted">
              Each transfer is one envelope: a checksummed batch with a stable idempotency key, attempt count, and delivery status.
              Failed envelopes can be retried — the same idempotency key is reused so the destination de-duplicates.
            </p>
            {envelopes.length === 0 ? (
              <div className="empty">
                <p><strong>No transfers yet.</strong></p>
                <p className="muted">Envelopes appear once a schedule fires or you click "Test send" on a destination.</p>
              </div>
            ) : (
              <>
              <TableToolbar
                search={tEnvelopes.search} setSearch={tEnvelopes.setSearch}
                page={tEnvelopes.page} setPage={tEnvelopes.setPage}
                pageSize={tEnvelopes.pageSize} setPageSize={tEnvelopes.setPageSize}
                totalPages={tEnvelopes.totalPages} totalRows={tEnvelopes.totalRows}
                hideExport
                searchPlaceholder="Search transfers…"
              />
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Destination</th>
                    <th>Items</th>
                    <th>Attempts</th>
                    <th>Status</th>
                    <th>Checksum</th>
                    <th>Last error</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(tEnvelopes.paged as unknown as Envelope[]).map((env) => {
                    const dest = destinations.find((d) => d.id === env.destinationId);
                    const pillClass =
                      env.status === "delivered"
                        ? "pill-success"
                        : env.status === "failed" || env.status === "abandoned"
                          ? "pill-error"
                          : env.status === "sending"
                            ? "pill-warning"
                            : "pill-muted";
                    return (
                      <tr key={env.id}>
                        <td className="soft">{new Date(env.createdAt).toLocaleString()}</td>
                        <td className="soft">{dest?.label ?? env.destinationId}</td>
                        <td className="soft">{env.itemCount}</td>
                        <td className="soft">{env.attemptCount}</td>
                        <td><span className={`pill ${pillClass}`}>{env.status}</span></td>
                        <td className="soft"><code>{env.payloadChecksum.slice(0, 12)}…</code></td>
                        <td className="soft">{env.lastError ?? ""}</td>
                        <td>
                          {env.status !== "delivered" && (
                            <button type="button" className="btn btn-link" disabled={busy} onClick={() => handleRetryEnvelope(env.id)}>
                              Retry
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </>
            )}
          </section>

          <section>
            <h2>Recent runs</h2>
            {recentRuns.length === 0 ? (
              <p className="muted">No runs yet. Runs will appear here once a schedule fires or a manual forward is triggered.</p>
            ) : (
              <>
              <TableToolbar
                search={tRuns.search} setSearch={tRuns.setSearch}
                page={tRuns.page} setPage={tRuns.setPage}
                pageSize={tRuns.pageSize} setPageSize={tRuns.setPageSize}
                totalPages={tRuns.totalPages} totalRows={tRuns.totalRows}
                hideExport
                searchPlaceholder="Search runs…"
              />
              <table className="data-table">
                <thead><tr><th>When</th><th>Destination</th><th>Status</th><th>Items</th><th>Message</th></tr></thead>
                <tbody>
                  {(tRuns.paged as unknown as typeof recentRuns).map((r) => (
                    <tr key={r.id}>
                      <td className="soft">{new Date(r.occurredAt).toLocaleString()}</td>
                      <td className="soft">{destinations.find((d) => d.id === r.destinationId)?.label ?? r.destinationId}</td>
                      <td><span className={`pill ${r.status === "succeeded" ? "pill-success" : r.status === "failed" ? "pill-error" : "pill-muted"}`}>{r.status}</span></td>
                      <td className="soft">{r.itemsForwarded ?? "—"}</td>
                      <td className="soft">{r.message ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function DestinationForm({
  workspaceId,
  destination,
  onClose,
  onSaved,
  onError
}: {
  workspaceId: string;
  destination?: Destination;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string | null) => void;
}) {
  const editing = Boolean(destination);
  const [label, setLabel]   = useState(destination?.label ?? "");
  const [kind, setKind]     = useState<Destination["kind"]>((destination?.kind as Destination["kind"]) ?? "dhis2");
  const [target, setTarget] = useState(destination?.target ?? "");
  const [status, setStatus] = useState<Destination["status"]>((destination?.status as Destination["status"]) ?? "active");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onError(null);
    setSubmitting(true);
    try {
      if (editing && destination) {
        await api.updateForwardingDestination(workspaceId, destination.id, { label, target, status });
      } else {
        await api.createForwardingDestination(workspaceId, { label, kind, target, status });
      }
      onSaved();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="card form-card" onSubmit={handleSubmit}>
      <h3>{editing ? "Edit destination" : "New destination"}</h3>
      <label className="form-field">
        <span>Label</span>
        <input required value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. National DHIS2" />
      </label>
      <label className="form-field">
        <span>Kind</span>
        <select value={kind} disabled={editing} onChange={(e) => setKind(e.target.value as Destination["kind"])}>
          {DESTINATION_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </label>
      <label className="form-field">
        <span>Target</span>
        <input required value={target} onChange={(e) => setTarget(e.target.value)} placeholder="https://dhis2.example.org/api or https://hooks.example.org/ingest" />
      </label>
      <label className="form-field">
        <span>Status</span>
        <select value={status} onChange={(e) => setStatus(e.target.value as Destination["status"])}>
          {DESTINATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <div className="form-actions">
        <button type="button" className="btn" disabled={submitting} onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>{editing ? "Save" : "Create"}</button>
      </div>
    </form>
  );
}

function ScheduleForm({
  workspaceId,
  destinationId,
  onClose,
  onSaved,
  onError
}: {
  workspaceId: string;
  destinationId: string;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string | null) => void;
}) {
  const [label, setLabel] = useState("");
  const [cron, setCron]   = useState("0 * * * *");
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onError(null);
    setSubmitting(true);
    try {
      await api.createForwardingSchedule(workspaceId, { destinationId, label, cron, enabled });
      onSaved();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="card form-card" onSubmit={handleSubmit}>
      <h3>New schedule</h3>
      <label className="form-field">
        <span>Label</span>
        <input required value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Hourly sync" />
      </label>
      <label className="form-field">
        <span>Cron</span>
        <input required value={cron} onChange={(e) => setCron(e.target.value)} placeholder="0 * * * *" />
      </label>
      <label className="form-field checkbox">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Enabled</span>
      </label>
      <div className="form-actions">
        <button type="button" className="btn" disabled={submitting} onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>Create</button>
      </div>
    </form>
  );
}
